import { Link, useSearchParams } from 'react-router-dom';
import {
  formatForumDate,
  formatForumLastReply,
  compactPersonName,
  forumThreadAvatarInitials,
  type ForumThreadSummary,
} from '@/features/course-forum/types';
import { courseForumThreadHref } from '@/lib/course-admin-navigation';

function ThreadBadges({ thread }: { thread: ForumThreadSummary }) {
  return (
    <>
      {thread.isPinned ? <span className="lms-course-forum-badge lms-course-forum-badge--pinned">Fijado</span> : null}
      {thread.isLocked ? <span className="lms-course-forum-badge lms-course-forum-badge--locked">Cerrado</span> : null}
    </>
  );
}

interface CourseForumThreadListItemProps {
  courseId: string;
  thread: ForumThreadSummary;
  staffView?: boolean;
}

function staffAuthorLabel(thread: ForumThreadSummary): string {
  const { author } = thread;
  if (author.isStaff) return author.displayName;
  const alias = author.anonAlias ?? author.displayName;
  const realName = author.authorRealName ? compactPersonName(author.authorRealName) : '';
  if (realName && realName !== alias) {
    return `${alias} (${realName})`;
  }
  return alias;
}

export function CourseForumThreadListItem({
  courseId,
  thread,
  staffView = false,
}: CourseForumThreadListItemProps) {
  const [searchParams] = useSearchParams();
  const initials = forumThreadAvatarInitials(thread);
  const authorLabel = staffView
    ? staffAuthorLabel(thread)
    : thread.author.isOwnPost && thread.author.anonAlias
      ? thread.author.anonAlias
      : thread.author.displayName;
  const threadHref = staffView
    ? courseForumThreadHref(courseId, thread.id, searchParams)
    : `/mis-cursos/${courseId}/foro/${thread.id}`;

  return (
    <Link to={threadHref} className="lms-course-forum-thread-item">
      <div className="lms-course-forum-thread-item__main">
        <div
          className={`lms-course-forum-thread-item__avatar${
            thread.author.isOwnPost ? ' lms-course-forum-thread-item__avatar--own' : ''
          }`}
          aria-hidden="true"
        >
          {initials}
        </div>
        <div className="lms-course-forum-thread-item__content">
          <div className="lms-course-forum-thread-item__title-row">
            <h3 className="lms-course-forum-thread-item__title">{thread.title}</h3>
            <span className="lms-course-forum-thread-item__author">{authorLabel}</span>
            <ThreadBadges thread={thread} />
          </div>
          {thread.excerpt ? (
            <p className="lms-course-forum-thread-item__excerpt">{thread.excerpt}</p>
          ) : null}
          <div className="lms-course-forum-thread-item__meta">
            <span className="lms-course-forum-thread-item__meta-item">
              <span className="material-symbols-outlined" aria-hidden="true">
                schedule
              </span>
              {formatForumDate(thread.createdAt)}
            </span>
            <span aria-hidden="true">·</span>
            <span>{formatForumLastReply(thread.updatedAt, thread.replyCount)}</span>
          </div>
        </div>
      </div>
      <div className="lms-course-forum-thread-item__aside">
        <div className="lms-course-forum-thread-item__replies">
          <span className="material-symbols-outlined" aria-hidden="true">
            chat_bubble
          </span>
          {thread.replyCount} {thread.replyCount === 1 ? 'respuesta' : 'respuestas'}
        </div>
        <span className="material-symbols-outlined lms-course-forum-thread-item__chevron" aria-hidden="true">
          chevron_right
        </span>
      </div>
    </Link>
  );
}
